import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Merge } from "lucide-react";

function CodeOption({ value, keep, setKeep, title, code, note }) {
  const on = keep === value;
  return (
    <button type="button" data-testid={`merge-keep-${value}`} onClick={() => setKeep(value)}
      className={`w-full text-left rounded-2xl border p-3.5 transition-colors ${on ? "border-[#723FFF] bg-[#F7F4FF]" : "border-[#EEEFEF] hover:border-[#d9ccff]"}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-[#06060C]">{title}</span>
        <span className="font-mono-data text-sm font-semibold text-[#5b2fd6] bg-white rounded-md px-2 py-0.5 border border-[#EEEFEF]">{code}</span>
      </div>
      <p className="text-[11px] text-[#9FA1A4] mt-1">{note}</p>
    </button>
  );
}

export function MergeIntoAgency({ detail, onMerged }) {
  const [open, setOpen] = useState(false);
  const [keep, setKeep] = useState("agency");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  if (detail.is_agency) return null;
  const merge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${detail.id}/merge-into-agency`, { keep, confirm_name: confirm });
      toast.success(`${detail.name} merged into Agency · IDs now use ${data.code}`);
      onMerged(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  return (
    <>
      <Button data-testid="merge-into-agency-button" variant="ghost" onClick={() => setOpen(true)} className="text-[#5b2fd6] hover:bg-[#F7F4FF]"><Merge className="h-4 w-4 mr-1.5" /> Merge into Agency</Button>
      {open && (
        <Dialog open onOpenChange={(o) => !o && !busy && setOpen(false)}>
          <DialogContent className="bg-white border-[#EEEFEF] max-w-md" data-testid="merge-into-agency-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">Merge {detail.name} into Agency?</DialogTitle>
              <DialogDescription>All projects, tasks, docs, files, leads, conversations, tickets and history move into Agency, and its portal users move to the Agency portal. This workspace and its URL are then deleted. This can't be undone.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <p className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">Which ID code should Agency use?</p>
              <CodeOption value="agency" keep={keep} setKeep={setKeep} title="Keep Agency's code" code={detail.agency_code} note={`${detail.name}'s IDs are renamed to ${detail.agency_code}-…`} />
              <CodeOption value="client" keep={keep} setKeep={setKeep} title={`Use ${detail.name}'s code`} code={detail.code} note={`Agency's own IDs are renamed to ${detail.code}-…`} />
              <p className="text-[11px] text-[#9FA1A4]">Old IDs and links keep redirecting to the new ones.</p>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs text-[#595D62]">Type <span className="font-semibold text-[#06060C]">{detail.name}</span> to confirm</p>
              <Input data-testid="merge-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="bg-[#F6F6FD] border-[#EEEFEF]" />
            </div>
            <DialogFooter>
              <Button variant="outline" data-testid="merge-cancel" disabled={busy} onClick={() => setOpen(false)} className="rounded-full border-[#EEEFEF]">Cancel</Button>
              <Button data-testid="merge-confirm" disabled={busy || confirm.trim().toLowerCase() !== detail.name.trim().toLowerCase()} onClick={merge} className="rounded-full bg-[#723FFF] text-white hover:bg-[#5b2fd6]">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Merge into Agency"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
